import { z } from "zod";

/**
 * EVERY VALUE HERE IS COMPILED INTO A BINARY SOMEBODY INSTALLS.
 *
 * On the web a wrong value is fixed by a deploy. On a phone it is fixed by an App Store
 * review and by every user choosing to update, which is why this file validates at module
 * load and refuses to start rather than letting `undefined` reach a fetch call and become
 * a request to `undefined/v1/session`.
 *
 * NEVER put a secret in a VITE_ variable. It is not "hidden in the bundle" — it is
 * shipped, extractable from any device, and unrevocable without a release.
 */
const schema = z.object({
  VITE_APP_ENV: z.enum(["dev", "staging", "production", "test"]),
  VITE_APP_NAME: z.string().min(1),
  VITE_APP_VERSION: z.string().min(1),
  /** The service. Baked per build mode — see .env.staging / .env.production. */
  VITE_API_URL: z.url(),
  /** Where the web build lives. Used for links that open the app, and for share URLs. */
  VITE_WEB_URL: z.url(),
  VITE_DEFAULT_LOCALE: z.string().min(2).default("en"),
  VITE_SUPPORTED_LOCALES: z.string().min(2).default("en"),

  /**
   * Reporting and analytics. Optional on purpose: a project's first week has neither, and
   * the seam in app/observability degrades to the console rather than failing to boot.
   * Neither value is a secret — both ship inside every binary — but both are validated here
   * so a typo is caught at start-up rather than by silence.
   */
  VITE_SENTRY_DSN: z.string().optional(),
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().optional(),

  /**
   * WHERE THIS BUILD LOOKS FOR AN OVER-THE-AIR UPDATE. Empty means it does not look, which
   * is how a build opts out of the channel entirely and is what dev carries.
   *
   * Per environment on purpose, and compiled in beside the environment stamp: a production
   * binary can only ever read the production channel, whatever a document it fetches says.
   * That is a second wall in front of `src/app/lib/ota.ts` rather than the wall itself —
   * the one that holds is the signing key in `capacitor.config.ts`, verified in Swift.
   *
   * ⚠️ `VITE_OTA_PUBLIC_KEY` sits beside this in the same env files and is deliberately NOT
   * read here. It is consumed by `capacitor.config.ts` at `cap sync` time and compiled into
   * the NATIVE SHELL, where an updated bundle cannot reach it. Reading it here would put a
   * copy in the JavaScript and invite somebody to verify with it — which would move the
   * decision into the very half of the app an attacker would be replacing.
   */
  VITE_OTA_UPDATE_URL: z.union([z.url(), z.literal("")]).optional(),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const missing = parsed.error.issues.map((i) => `  - ${i.path.join(".")}: ${i.message}`);
  throw new Error(
    [
      "Environment is not usable, so the app refuses to start:",
      ...missing,
      "",
      "Fix: `make sync-env` writes .env.dev.local from the root .env.",
      "For a staging or production build the values live in .env.staging / .env.production.",
    ].join("\n"),
  );
}

const raw = parsed.data;

/**
 * ON A PHONE, `localhost` IS THE PHONE — never this Mac.
 *
 * `make ios-dev` works out an address the handset can actually reach and serves the bundle
 * from it. The SERVICE address was left at whatever the env file said, which locally is
 * `http://localhost:8787` — so on the device the bundle loaded perfectly and then every
 * request failed, with nothing on screen pointing at the cause.
 *
 * The answer is one line because the dev server does the work: it proxies `/v1` through to
 * the service (see `server.proxy` in vite.config.ts), so on a device the service lives at
 * the very origin the app was served from. That holds for both routes the phone can take,
 * which is the point — there is no second address, no port table, and no scheme to keep in
 * step:
 *
 *   Wi-Fi     http://<mac>.local:5273         -> /v1 proxied to the service
 *   tailnet    https://<mac>.<tailnet>/        -> /v1 proxied to the service
 *
 * It also makes every request SAME-ORIGIN, so CORS is not involved on the phone at all.
 *
 * Untouched everywhere else. Staging and production carry real hostnames, and a bundled dev
 * build (served from `capacitor://localhost`) has no dev server in front of it, so both
 * fall through to the configured value.
 */
function resolveApiUrl(configured: string): string {
  if (raw.VITE_APP_ENV !== "dev" || typeof window === "undefined") return configured;

  const loopback = new Set(["localhost", "127.0.0.1", "[::1]"]);
  // Only rewrite a loopback address. Anything explicit was chosen deliberately.
  if (!loopback.has(new URL(configured).hostname)) return configured;

  const servedFrom = window.location.hostname;
  // Served over loopback means a browser on this Mac, where the configured address is right
  // and no proxy assumption should be made.
  if (!servedFrom || loopback.has(servedFrom)) return configured;

  return window.location.origin;
}

export const env = {
  appEnv: raw.VITE_APP_ENV,
  appName: raw.VITE_APP_NAME,
  version: raw.VITE_APP_VERSION,
  apiUrl: resolveApiUrl(raw.VITE_API_URL).replace(/\/$/, ""),
  webUrl: raw.VITE_WEB_URL.replace(/\/$/, ""),
  defaultLocale: raw.VITE_DEFAULT_LOCALE,
  locales: raw.VITE_SUPPORTED_LOCALES.split(",").map((l) => l.trim()).filter(Boolean),
  isProduction: raw.VITE_APP_ENV === "production",
  /**
   * `null` rather than an empty string, so "this build has no channel" is a value a caller
   * has to handle rather than one that falls through a truthiness check into `fetch("")`.
   */
  otaUpdateUrl: raw.VITE_OTA_UPDATE_URL?.trim() || null,
  /**
   * Settings names the build on every non-production identity. Two builds that look
   * identical are a long afternoon — but a bar pinned above every screen was paying for
   * that with permanent vertical space, so the answer lives one tap away instead.
   */
  showsBuildDetail: raw.VITE_APP_ENV !== "production",
} as const;

export type AppEnv = typeof env.appEnv;
